import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { V4StorefrontSearchRequest } from '@zola/svc-marketplace-ts-types';

import _groupBy from 'lodash/groupBy';

import { clientAwardGroupsEnumToAwardGroupsEnum } from '~/meta/awards';
import {
  VendorSearchRequest,
  VendorSearchRequestType,
} from '~/pages/couples/explore/components/SearchResults/types/types';
import { formatDate, isValidDate } from '~/util/dateUtils';
import { isSearchLocation } from '~/util/searchUtils';

import { MAX_RANGE_INDICATOR } from './helpers';

export const buildBackendVendorSearchRequestFromSearchProperties = ({
  vendorSearch,
  pageSize,
  shouldInsertBudgetQuiz,
  shouldInsertMiniQuiz,
  identifiedAsVendor,
  isInHoldoutGroupExperiment,
  userAgent,
}: {
  vendorSearch: VendorSearchRequest;
  pageSize: number;
  shouldInsertMiniQuiz: boolean;
  shouldInsertBudgetQuiz: boolean;
  identifiedAsVendor: boolean;
  isInHoldoutGroupExperiment: boolean;
  userAgent?: string | undefined;
}) => {
  const numQuizzes = [shouldInsertMiniQuiz, shouldInsertBudgetQuiz].filter(Boolean).length;
  // TODO: Combine the page size & offset adjustments in the buildSearchRequest call?
  const pageSizeAdjustment = (vendorSearch.page || 1) === 1 ? numQuizzes : 0;
  const offsetAdjustment = (vendorSearch.page || 1) > 1 ? numQuizzes : 0;
  const allowUnclaimedListings = true;
  const boostFeaturedStorefronts =
    !userAgent?.toLowerCase().includes('googlebot') &&
    (identifiedAsVendor || !isInHoldoutGroupExperiment);

  return buildSearchRequest(
    vendorSearch,
    pageSize - pageSizeAdjustment,
    allowUnclaimedListings,
    offsetAdjustment,
    boostFeaturedStorefronts
  );
};

/**
 * Takes a vendor search, used in the SRP, and turns it into a payload to the
 * backend.
 *
 * @param vendorSearch
 * @param pageSize
 * @param allowUnclaimedListings
 * @param offsetAdjustment
 * @returns
 */
export const buildSearchRequest = (
  vendorSearch: VendorSearchRequest,
  pageSize: number,
  allowUnclaimedListings: boolean,
  offsetAdjustment = 0,
  boostFeaturedStorefronts: boolean | undefined = undefined
): VendorSearchRequestType => {
  const {
    awardGroups,
    selectedLocation,
    selectedTaxonomy,
    selectedFacets,
    price,
    capacity,
    availabilityDates,
    metroTypes,
    sorting,
    searchName,
    page: currentPage,
    seoOptimized = false,
  } = vendorSearch;

  const request: VendorSearchRequestType = {
    offset: (currentPage - 1) * pageSize - offsetAdjustment,
    limit: pageSize,
    locationMethod: 'MARKET',
    pointRadius: null,
    metroTypes: null,
    vendorMarketId: null,
    city: null,
    stateProvince: null,
    vendorType: null,
    price: null,
    capacity: null,
    facetGroups: null,
    targetDates: null,
    sortBy: null,
    sortOrder: null,
    allowUnclaimedListings,
    searchName: searchName || null,
    boostFeaturedStorefronts: boostFeaturedStorefronts ?? null,
    awardGroups: null,
    priceTier: null,
    seoOptimized,
    excludeBookedStorefronts: null,
    excludeInquiredStorefronts: null,
  };

  if (awardGroups?.[0]?.length) {
    request.awardGroups = clientAwardGroupsEnumToAwardGroupsEnum(awardGroups);
  }

  if (isSearchLocation(selectedLocation)) {
    const { storefrontSearchPayload, geoCoordinates } = selectedLocation;

    request.vendorMarketId = storefrontSearchPayload.vendorMarketId || null;

    if (selectedTaxonomy?.key === 'wedding-venues') {
      request.locationMethod = 'ADJACENT_MARKET';
    } else {
      request.locationMethod = 'MARKET';
    }

    // Add this all the time if available. BE will use when needed.
    if (geoCoordinates?.latitude && geoCoordinates?.longitude) {
      request.pointRadius = {
        latitude: geoCoordinates.latitude,
        longitude: geoCoordinates.longitude,
        radiusMiles: null,
      };
    }

    request.city = storefrontSearchPayload?.city || null;
    request.stateProvince = storefrontSearchPayload?.stateProvince || null;
  }

  if (selectedTaxonomy) {
    const vendorType = getVendorType(selectedTaxonomy.key);
    if (vendorType) {
      request.vendorType = vendorType as V4StorefrontSearchRequest.VendorTypeEnum;
    }
  }

  if (price) {
    if (request.price) {
      request.price.priceType = 'FULL';
    }
    if (price.min) {
      const minPriceCents = price.min * 100; // dollars to cents

      if (request.price && request.price?.priceCents) {
        request.price.priceCents.min = minPriceCents;
      } else {
        request.price = { priceType: 'FULL', priceCents: { min: minPriceCents, max: null } };
      }
    }
    if (price.max && price.max !== MAX_RANGE_INDICATOR) {
      const maxPriceCents = price.max * 100; // dollars to cents

      if (request.price && request.price?.priceCents) {
        request.price.priceCents.max = maxPriceCents;
      } else {
        request.price = { priceType: 'FULL', priceCents: { max: maxPriceCents, min: null } };
      }
    }

    // Don't submit invalid price searches
    if (
      request.price?.priceCents?.min &&
      request.price?.priceCents?.max &&
      Number(request.price?.priceCents?.min) > Number(request.price?.priceCents?.max)
    ) {
      request.price = null;
    }
  }

  if (capacity) {
    if (capacity.min) {
      if (request.capacity) {
        request.capacity.min = capacity.min;
      } else {
        request.capacity = { max: null, min: capacity.min };
      }
    }
    if (capacity.max) {
      if (capacity.max !== MAX_RANGE_INDICATOR) {
        if (request.capacity) {
          request.capacity.max = capacity.max;
        } else {
          request.capacity = { min: null, max: capacity.max };
        }
      }
    }
  }

  if (availabilityDates) {
    if (availabilityDates.every(isValidDate)) {
      // @ts-expect-error dates are weird
      request.targetDates = availabilityDates.map((availabilityDate) =>
        formatDate(availabilityDate, 'YYYY-MM-DD')
      );
    }
  }

  if (selectedFacets) {
    const groups = _groupBy(selectedFacets, 'parentKey');
    const parentKeys = Object.keys(groups);
    request.facetGroups = parentKeys.map((parentKey) => {
      return groups[parentKey].map((facet) => facet.key);
    });
  }

  if (metroTypes) {
    request.metroTypes = metroTypes;
  }

  if (sorting && sorting.sortBy) {
    request.sortBy = sorting.sortBy;
    request.sortOrder = sorting.sortOrder;
  }

  return request;
};
